import React from "react";
import { Card, Col, Row, Space, Table, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import type { DeforestationAssessment, DeforestationSource } from "../types";
import { EudrTermHint } from "./EudrTermHint";
import { MobileDataCards } from "./MobileDataCards";
import { useNarrowViewport } from "../ux/useNarrowViewport";

const statusColor: Record<string, string> = {
  ok: "success",
  failed: "error",
  not_configured: "warning",
  disabled: "default",
  skipped: "default",
};

type Props = {
  deforestation: DeforestationAssessment | null | undefined;
  /** Compact layout under validation findings vs passport Descriptions. */
  compact?: boolean;
};

function providerLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  id: string | undefined,
): string {
  if (!id) return t("common.dash");
  return t(`compliance.passport.providerLabels.${id}`, { defaultValue: id });
}

function statusLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  status: string | undefined,
): string {
  if (!status) return t("common.dash");
  return t(`compliance.deforestationComparison.status.${status}`, {
    defaultValue: status,
  });
}

function formatCoeff(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toFixed(4);
}

/** US-126: side-by-side / merged per-provider scores + combined weighted result. */
export function DeforestationComparisonPanel({ deforestation, compact }: Props) {
  const { t } = useTranslation();
  const isNarrow = useNarrowViewport("md");

  if (!deforestation || deforestation.skipped) {
    return null;
  }

  const sources: DeforestationSource[] = deforestation.sources ?? [];
  const combineRule =
    deforestation.combine_rule ?? "weighted_average_normalized";

  const primaryCards = sources.filter((s) =>
    ["gee", "gfw", "copernicus", "nicfi", "jrc", "mapbiomas", "planet", "satelligence", "stub"].includes(String(s.id ?? "")),
  );
  const cards = primaryCards.length > 0 ? primaryCards : sources;

  return (
    <div
      data-testid="deforestation-comparison"
      style={{ marginBottom: compact ? 12 : 16 }}
    >
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        <Space size="small">
          <span>{t("compliance.deforestationComparison.title")}</span>
          <EudrTermHint term="deforestationRisk" />
          <EudrTermHint term="providerMode" testId="eudr-term-hint-providerMode" />
        </Space>
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        {t("compliance.deforestationComparison.combineRule", {
          rule: t(`compliance.deforestationComparison.rules.${combineRule}`, {
            defaultValue: combineRule,
          }),
        })}
      </Typography.Paragraph>

      {cards.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          {cards.map((s) => {
            const id = String(s.id ?? "?");
            const ok = s.status === "ok";
            return (
              <Col xs={24} sm={12} md={8} key={`${id}-${s.status}-${s.mode}`}>
                <Card
                  size="small"
                  title={providerLabel(t, id)}
                  extra={
                    <Tag color={statusColor[s.status ?? ""] ?? "default"}>
                      {statusLabel(t, s.status)}
                    </Tag>
                  }
                >
                  <div>
                    {t("compliance.deforestationComparison.coefficient")}:{" "}
                    <strong>{ok ? formatCoeff(s.coefficient) : t("common.dash")}</strong>
                  </div>
                  <div>
                    {t("compliance.passport.deforestationMode")}: {s.mode ?? t("common.dash")}
                  </div>
                  <div>
                    {t("compliance.deforestationComparison.weightApplied")}:{" "}
                    {s.weight_applied != null
                      ? s.weight_applied.toFixed(4)
                      : s.weight != null
                        ? String(s.weight)
                        : t("common.dash")}
                  </div>
                  <div>
                    {t("compliance.deforestationComparison.datasetVersion")}:{" "}
                    {s.dataset_version ?? t("common.dash")}
                  </div>
                  <div>
                    {t("compliance.deforestationComparison.assessedAt")}:{" "}
                    {s.assessed_at ?? t("common.dash")}
                  </div>
                  {!ok && s.error && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {s.error}
                    </Typography.Text>
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Card size="small" type="inner" title={t("compliance.deforestationComparison.combinedTitle")}>
        <Row gutter={16}>
          <Col>
            {t("compliance.passport.deforestationProvider")}:{" "}
            <strong>
              {providerLabel(t, deforestation.provider)}
              {deforestation.interim || deforestation.mode === "mock"
                ? ` (${t("compliance.deforestationComparison.interimDemo")})`
                : ""}
            </strong>
          </Col>
          <Col>
            {t("compliance.deforestationComparison.combinedScore")}:{" "}
            <strong>
              {formatCoeff(deforestation.coefficient)}
              {deforestation.risk_class ? ` (${deforestation.risk_class})` : ""}
            </strong>
          </Col>
          <Col>
            {t("compliance.passport.deforestationCutoff")}:{" "}
            {deforestation.cutoff_date ?? t("common.dash")}
          </Col>
        </Row>
      </Card>

      {sources.length > 0 &&
        (isNarrow ? (
          <MobileDataCards
            testId="deforestation-sources-mobile"
            items={sources}
            rowKey={(row) => `${row.id ?? "?"}-${row.status}`}
            title={(row) => providerLabel(t, row.id)}
            fields={(row) => [
              {
                label: t("compliance.deforestationComparison.columnStatus"),
                value: (
                  <Tag color={statusColor[row.status] ?? "default"}>{statusLabel(t, row.status)}</Tag>
                ),
              },
              {
                label: t("compliance.deforestationComparison.coefficient"),
                value: formatCoeff(row.coefficient),
              },
              {
                label: t("compliance.deforestationComparison.weightApplied"),
                value:
                  row.weight_applied != null
                    ? row.weight_applied.toFixed(4)
                    : row.weight != null
                      ? String(row.weight)
                      : "—",
              },
              {
                label: t("compliance.deforestationComparison.datasetVersion"),
                value: row.dataset_version ?? "—",
              },
            ]}
          />
        ) : (
        <Table
          size="small"
          style={{ marginTop: 12 }}
          pagination={false}
          rowKey={(row, idx) => `${row.id ?? "?"}-${row.status}-${idx}`}
          dataSource={sources}
          columns={[
            {
              title: t("compliance.deforestationComparison.columnProvider"),
              dataIndex: "id",
              render: (id: string) => providerLabel(t, id),
            },
            {
              title: t("compliance.deforestationComparison.columnStatus"),
              dataIndex: "status",
              render: (st: string) => (
                <Tag color={statusColor[st] ?? "default"}>{statusLabel(t, st)}</Tag>
              ),
            },
            {
              title: t("compliance.deforestationComparison.coefficient"),
              dataIndex: "coefficient",
              render: (v: number | null) => formatCoeff(v),
            },
            {
              title: t("compliance.deforestationComparison.weightApplied"),
              dataIndex: "weight_applied",
              render: (_: unknown, row: DeforestationSource) =>
                row.weight_applied != null
                  ? row.weight_applied.toFixed(4)
                  : row.weight != null
                    ? String(row.weight)
                    : "—",
            },
            {
              title: t("compliance.deforestationComparison.datasetVersion"),
              dataIndex: "dataset_version",
              render: (v: string | null | undefined) => v ?? "—",
            },
          ]}
        />
        ))}
    </div>
  );
}
